import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';

import { Button } from '@cw-ui/form-components';
import { useI18nTranslation } from 'platform-i18n-utils';

import { I18N_KEYS, SUBMIT_LABEL, SUBMITTING_LABEL } from '../../constants';
import { useTicketFormState, useTicketSubmit } from '../../hooks';
import { translateTicketConfig } from '../../utils/translate-ticket-config';
import TicketFormBanner from '../ticket-page/ticket-form-banner';
import TicketSubmissionToast from '../ticket-page/ticket-submission-toast';
import { CARD_BORDER_RADIUS, CARD_BOX_SHADOW, CARD_MAX_WIDTH, CARD_OVERLAP } from './layout';
import TicketFormFields from './ticket-form-fields';

// The banner is rendered here (not as a sibling above this component), so
// the banner and the card below it are both owned by this one component. The
// card is intentionally narrower than the banner and overlaps its bottom
// edge (negative margin-top) with rounded corners + a shadow, matching the
// reference design's "floating card over the hero banner" look. (Not using a
// native <form> element: @cw-ui/form-components' FileUpload renders an
// internal "remove file" IconButton with no explicit `type`, which the
// browser would default to `type="submit"` inside a real <form>, so a real
// form element risks submitting the ticket when a file is removed.)
const TicketForm = ({ config: serviceConfig, eid }) => {
  const { t } = useI18nTranslation();
  // Service text arrives with optional i18n keys; translate once here so labels,
  // placeholders, banner, submit button and validation messages all agree.
  const config = useMemo(() => translateTicketConfig(serviceConfig, t), [serviceConfig, t]);
  const fields = config?.fields ?? [];
  const { values, setFieldValue, touchField, prepareSubmit, errors } = useTicketFormState(config);
  const { submitTicket, submitting } = useTicketSubmit(eid);
  const [submissionOutcome, setSubmissionOutcome] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);

  const isLocked = submissionOutcome !== null;

  const handleSubmit = useCallback(async () => {
    const payload = prepareSubmit();
    if (!payload) {
      return;
    }

    try {
      const result = await submitTicket(payload.body, payload.files);
      if (result?.ticketId) {
        setSubmissionOutcome({ type: 'success', ticketId: result.ticketId });
        setToastOpen(true);
      } else {
        setSubmissionOutcome({ type: 'failure' });
        setToastOpen(true);
      }
    } catch {
      setSubmissionOutcome({ type: 'failure' });
      setToastOpen(true);
    }
  }, [prepareSubmit, submitTicket]);

  const handleToastClose = useCallback(() => {
    setToastOpen(false);
  }, []);

  return (
    <Box data-testid="ticket-form" sx={{ width: '100%', backgroundColor: 'background.default', pb: 5 }}>
      <TicketFormBanner title={config?.formTitle} subtitle={config?.formSubtitle} />

      <Box
        data-testid="ticket-form-card"
        sx={{
          position: 'relative',
          maxWidth: CARD_MAX_WIDTH,
          mx: 'auto',
          mt: `-${CARD_OVERLAP}`,
          backgroundColor: 'background.paper',
          borderRadius: CARD_BORDER_RADIUS,
          boxShadow: CARD_BOX_SHADOW,
          px: 4,
          py: 3
        }}
      >
        <TicketFormFields
          fields={fields}
          values={values}
          onFieldChange={setFieldValue}
          onFieldBlur={touchField}
          fieldErrors={errors}
          titlePrefix={config?.titlePrefix}
          disabled={isLocked}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            type="button"
            data-testid="ticket-form-submit"
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            size="medium"
            disabled={isLocked || submitting}
          >
            {submitting
              ? t(I18N_KEYS.SUBMITTING_LABEL, { defaultValue: SUBMITTING_LABEL })
              : (config?.submitLabel ?? t(I18N_KEYS.SUBMIT_LABEL, { defaultValue: SUBMIT_LABEL }))}
          </Button>
        </Box>
      </Box>

      <TicketSubmissionToast
        outcome={submissionOutcome}
        open={toastOpen}
        onClose={handleToastClose}
      />
    </Box>
  );
};

TicketForm.propTypes = {
  config: PropTypes.shape({
    formTitle: PropTypes.string,
    formTitleKey: PropTypes.string,
    formSubtitle: PropTypes.string,
    formSubtitleKey: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.object),
    submitLabel: PropTypes.string,
    submitLabelKey: PropTypes.string,
    titlePrefix: PropTypes.string
  }).isRequired,
  eid: PropTypes.string.isRequired
};

export default TicketForm;
